import { ACCESS_TOKEN } from '../assets/constants'
import { ensureValidAccessToken } from '../services/auth/tokenRefresh'

type Phase = 'auth' | 'profile' | 'chart' | 'open'

const PHASE_RANK: Record<Phase, number> = {
	auth: 0,
	profile: 1,
	chart: 2,
	open: 3
}

let phase: Phase = 'auth'
const listeners = new Set<() => void>()
let openTimer: ReturnType<typeof setTimeout> | null = null

// /v2/security/all is ~1MB and shares the HTTP/2 connection with the chart's
// first symbol lookup + candle request. On chart routes we hold it back until
// the first candle request is out (or this fallback elapses) so it does not
// starve the critical path. Nothing on the chart route needs the full list:
// the chart resolves its own symbols via POST /v2/security.
const SECURITIES_RELEASE_FALLBACK_MS = 3000
let securitiesReleased = false
let securitiesReleaseTimer: ReturnType<typeof setTimeout> | null = null

function notify() {
	listeners.forEach((listener) => listener())
}

function setPhase(next: Phase) {
	if (PHASE_RANK[next] <= PHASE_RANK[phase]) return
	phase = next
	notify()
}

function isAuthRoute() {
	const path = window.location.pathname
	return path.includes('/login') || path.includes('/auth-status') || path.includes('/signup')
}

function isSecuritiesListRequest(url: string) {
	return url.includes('/v2/security/all')
}

function isEmbeddedContext() {
	const path = window.location.pathname
	return path.startsWith('/embedded') || localStorage.getItem('isEmbedded') === 'true'
}

// Routes where a chart mounts on load and /candles/bulk is guaranteed to fire.
function isChartRoute() {
	const path = window.location.pathname
	return path === '/' || path.startsWith('/chart')
}

function shouldDeferSecuritiesList() {
	return isChartRoute() && !isEmbeddedContext()
}

function releaseSecuritiesList() {
	if (securitiesReleased) return
	securitiesReleased = true
	if (securitiesReleaseTimer) {
		clearTimeout(securitiesReleaseTimer)
		securitiesReleaseTimer = null
	}
	notify()
}

function isChartRequest(url: string) {
	if (isSecuritiesListRequest(url)) return false
	return (
		url.includes('/user-dashboards') ||
		url.includes('/supercharts/') ||
		url.includes('/v2/security') ||
		url.includes('/candles/bulk') ||
		url.includes('/user-security-expression')
	)
}

function requiredPhase(url: string): Phase {
	if (isSecuritiesListRequest(url)) return shouldDeferSecuritiesList() ? 'open' : 'profile'
	if (url.includes('/user/me')) return 'profile'
	if (isChartRequest(url)) return 'chart'
	return 'open'
}

function canProceed(url: string) {
	if (!localStorage.getItem(ACCESS_TOKEN) || isAuthRoute()) return true
	if (isSecuritiesListRequest(url) && securitiesReleased) return true
	return PHASE_RANK[phase] >= PHASE_RANK[requiredPhase(url)]
}

export function waitForInitialLoadSlot(url: string): Promise<void> {
	if (canProceed(url)) return Promise.resolve()
	return new Promise((resolve) => {
		const check = () => {
			if (!canProceed(url)) return
			listeners.delete(check)
			resolve()
		}
		listeners.add(check)
	})
}

export function markProfileLoaded() {
	setPhase('chart')
	if (openTimer) clearTimeout(openTimer)
	openTimer = setTimeout(() => setPhase('open'), 8000)
	if (!securitiesReleased && !securitiesReleaseTimer) {
		securitiesReleaseTimer = setTimeout(releaseSecuritiesList, SECURITIES_RELEASE_FALLBACK_MS)
	}
}

export function markChartDataRequested() {
	setPhase('open')
	releaseSecuritiesList()
}

void ensureValidAccessToken().finally(() => {
	setPhase('profile')
	if (!openTimer) {
		openTimer = setTimeout(() => setPhase('open'), 8000)
	}
})
